import classnames from 'classnames';
import React, {useRef} from 'react';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import useAutoFocusReturn from 'lib/hooks/useAutoFocusReturn';
import usePrevious from 'lib/hooks/usePrevious';

import AnnotationEditor from './AnnotationEditor';
import {isSupplementAnchor} from './annotationAnchors';
import {NOTE_TOOLTIP_PROPS} from './annotationConstants';
import {useAnnotationModeStore} from './AnnotationModeStore';
import type {QuestionNote} from './annotationNotes';
import AnnotationNotesPanelList from './AnnotationNotesPanelList';
import {selectIsNoteListed} from './annotationSelectors';
import {useCanViewAnnotations} from './useCanViewAnnotations';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';
import {useRegionEdit} from './useRegionEdit';

const PANEL_TITLE_ID = 'annotation-notes-panel-title';

/** The Figma's width (2692:5224 placed in 3478:24413); panel widths have no tokens. */
const PANEL_WIDTH_CLASS = 'w-[343px]';

/** The note whose editor is open, and the row its popover hangs below. */
interface PanelEdit {
  note: QuestionNote;
  row: HTMLLIElement;
}

interface AnnotationNotesPanelProps {
  /** The question whose notes are listed; undefined while one loads. */
  questionId: string | undefined;
  /** Off where the host already frames the seam, so the divider stays 1px. */
  showStartBorder?: boolean;
}

/**
 * The question's notes, docked beside it rather than over it. Stays mounted
 * while closed so the width can animate; `inert` keeps its controls untabbable.
 */
export default function AnnotationNotesPanel({
  questionId,
  showStartBorder = true
}: AnnotationNotesPanelProps) {
  const canViewAnnotations = useCanViewAnnotations();
  const canEditAnnotations = useIsAnnotationModeAvailable();
  const [editingNote, openEditor, closeEditor] = useRegionEdit<PanelEdit>();
  const isOpen = useAnnotationModeStore((state) => state.isNotesPanelOpen);
  const isReadOnly = useAnnotationModeStore((state) => state.isReadOnly);
  const closeNotesPanel = useAnnotationModeStore((state) => state.closeNotesPanel);
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = usePrevious(isOpen);
  // Never true on a remount: PracticeView remounts per question navigation, and
  // an already-open panel must not steal focus from the question.
  const justOpened = isOpen && wasOpen === false;
  const restoreFocus = useAutoFocusReturn(panelRef, justOpened);
  const isEditedNoteListed = useAnnotationModeStore((state) =>
    selectIsNoteListed(state, editingNote?.note.regionId, editingNote?.note.annotation.id)
  );
  const activeEdit = isEditedNoteListed ? editingNote : null;

  // Focus must leave before `inert` pins it to a disabled control. Only keyboard
  // closes then call `restoreFocus`: the indicator's peek popover opens on focus,
  // so handing it back after a mouse close would spring the peek open again.
  const close = () => {
    closeEditor();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    closeNotesPanel();
  };

  // Stable: AnnotationPopover feeds it to three global listeners.

  if (!canViewAnnotations || isReadOnly) {
    return null;
  }

  return (
    <>
      <aside
        ref={panelRef}
        aria-labelledby={PANEL_TITLE_ID}
        inert={!isOpen}
        data-testid='annotation-notes-panel'
        className={classnames(
          'shrink-0 overflow-hidden transition-[width] duration-[var(--duration-fast)] ease-linear motion-reduce:transition-none',
          isOpen ? PANEL_WIDTH_CLASS : 'w-x0'
        )}
        onKeyDown={(event) => {
          // preventDefault makes the document-level mode-exit Escape listener yield.
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            restoreFocus();
          }
        }}
      >
        {/* Fixed width: clipped rather than reflowed while the outer box animates. */}
        <div
          className={classnames(
            // No border-solid: without preflight it paints 3px UA borders on unsized sides.
            'background-subtle border-border-primary flex h-full flex-col',
            showStartBorder && 'border-l',
            PANEL_WIDTH_CLASS
          )}
        >
          <header className='gap-x1 px-x2 py-x1-half background-primary border-border-primary flex shrink-0 items-center justify-between border-b'>
            {/* Icon inside the title so it inherits the 15px size; font-normal and
                m-x0 shed the h2's UA bold and legacy margin. */}
            <Text
              as='h2'
              id={PANEL_TITLE_ID}
              size='m'
              color='primary'
              className='gap-x1 m-x0 flex items-center font-normal'
            >
              <Icon icon='notes' iconStyle='regular' />
              Notes
            </Text>
            <IconButton
              ref={closeButtonRef}
              icon='times'
              iconStyle='regular'
              label='Close notes'
              size='s'
              tooltip='Close'
              tooltipProps={NOTE_TOOLTIP_PROPS}
              onClick={(event: React.MouseEvent) => {
                close();
                // detail 0 marks a keyboard-activated click (Enter/Space).
                if (event.detail === 0) {
                  restoreFocus();
                }
              }}
            />
          </header>
          <div className='min-h-x0 grow overflow-y-auto'>
            <AnnotationNotesPanelList
              questionId={questionId}
              editingNoteId={activeEdit?.note.annotation.id}
              onEditNote={
                canEditAnnotations
                  ? (note, row) => openEditor({note, row}, note.regionId)
                  : undefined
              }
            />
          </div>
        </div>
      </aside>
      {/* Outside the aside: a portal's events bubble through the React tree, so
          an Escape for the editor would close the panel too. */}
      {isOpen && activeEdit && (
        <AnnotationEditor
          regionId={activeEdit.note.regionId}
          activeAnnotation={{id: activeEdit.note.annotation.id}}
          anchorEl={activeEdit.row}
          allowUnderline={!isSupplementAnchor(activeEdit.note.annotation.anchor)}
          onClose={() => {
            // Outlives the pencil, which Remove takes away.
            closeButtonRef.current?.focus({preventScroll: true});
            closeEditor();
          }}
        />
      )}
    </>
  );
}
